import React, { useState, useEffect } from 'react';
import { Modal, Typography } from '@mui/material';
import { Close, Add, AccountCircle } from '@mui/icons-material';
import { SearchOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import styled from 'styled-components';
import { get, map, filter } from 'lodash';
import COLOR from 'utils/constants/color';
import { getAvatar } from 'utils/assets/images/lootie';
import { makeStyles } from 'utils/styles/mui';

const AVATAR_COLORS = [
    '#DF52D2',
    '#FC6260',
    '#3FD0FF',
    '#f79835',
    '#3FD0FF'
];

const ClassAnnouncment = styled.div`
    margin-bottom: 20px;
    width: 100%;
    align-self: center;
    display: flex;
    align-items: center;
    cursor: pointer;
    text-transform: capitalize;
    text-overflow: ellipsis;
    color: #000;
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    transition: all 0.2s ease;
    
    &:hover {
        opacity: 0.8;
        transform: translateX(4px);
    }
`;

const UserModal = styled.div`
    left: 50%;
    max-width: 450px;
    padding: 0px 24px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    animation: fadeInScale 0.3s ease-out;
    
    @keyframes fadeInScale {
        from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
        }
        to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
        }
    }
`;

const UserPopUpArea = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 24px;
    position: relative;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const CloseBtn = styled.div`
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    position: absolute;
    top: 16px;
    right: 16px;
    background-color: rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    z-index: 1;
    
    &:hover {
        background-color: rgba(0, 0, 0, 0.1);
        transform: rotate(90deg);
    }
`;

const SearchArea = styled.div`
    border-radius: 12px;
    height: 44px;
    display: flex;
    flex-direction: row;
    padding: 0 12px;
    background: linear-gradient(135deg, rgba(75, 156, 161, 0.1) 0%, rgba(75, 156, 161, 0.15) 100%);
    border: 1.5px solid rgba(75, 156, 161, 0.2);
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
    transition: all 0.3s ease;
    
    &:focus-within {
        border-color: rgba(75, 156, 161, 0.5);
        background: linear-gradient(135deg, rgba(75, 156, 161, 0.15) 0%, rgba(75, 156, 161, 0.2) 100%);
        box-shadow: 0 4px 12px rgba(75, 156, 161, 0.2);
    }
`;

const SearchIconArea = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(75, 156, 161, 0.15);
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 10px;
    color: rgba(75, 156, 161, 0.8);
`;

const SearchInput = styled.input`
    width: 100%;
    height: 100%;
    font-size: 15px;
    background: transparent;
    border: none;
    outline: none;
    color: ${COLOR.black100};
    font-weight: 500;
    
    &::placeholder,
    &::-webkit-input-placeholder {
        color: ${COLOR.gray100};
        opacity: 0.7;
    }
`;

const UserArea = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    padding-top: 0px;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
        
        &:hover {
            background: rgba(0, 0, 0, 0.3);
        }
    }
`;

const UserList = styled.div`
    min-height: 56px;
    width: 100%;
    cursor: pointer;
    font-size: 12px;
    position: relative;
    margin-bottom: 8px;
    background: ${props => props.active ? 'rgba(75, 156, 161, 0.08)' : 'transparent'};
    border: none;
    border-radius: 12px;
    color: #fff;
    text-align: left;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 10px 12px;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${props => props.active ? 'rgba(75, 156, 161, 0.12)' : 'rgba(0, 0, 0, 0.03)'};
        transform: translateX(4px);
    }
    
    &:active {
        transform: translateX(2px) scale(0.98);
    }
`;

const UserRow = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 100%;
    gap: 10px;
    position: relative;
`;

const AvtarContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background-color: ${props => props.bgColor || 'transparent'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
    
    &:hover {
        transform: scale(1.05);
    }
`;

const NameCol = styled.div`
    display: flex;
    align-items: center;
    width: 83%;
    flex-direction: column;
`;

const NameInnerRow = styled.div`
    display: flex;
    width: 100%;
    color: #fff;
    font-weight: bold;
    text-transform: capitalize;
`;

const OnlineIndicater = styled.div`
    height: 10px;
    width: 10px;
    background-color: #44D824;
    border-radius: 50%;
    position: absolute;
    right: 0px;
    top: 0px;
`;

const useStyles = makeStyles((theme) => ({
    addIcon: {
        color: 'rgb(255, 255, 255)',
        fontSize: '20px',
        marginRight: ' 5px'
    },
    usericonPopUp: {
        color: COLOR.gray100,
        fontSize: '30px',
        marginRight: ' 5px'
    },
    newMessageTitle: {
        marginTop: '0px',
        marginBottom: '20px',
        fontWeight: 700,
        textAlign: "center",
        fontSize: '20px',
        color: COLOR.black100,
        letterSpacing: '-0.5px'
    }
}));

const getAvatarColor = (userId) => {
    if (!userId) return AVATAR_COLORS[0];
    const index = Math.abs(userId.toString().split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index];
};

const NewMessageButton = ({ activeUser, selectedUser, setSelectedUser }) => {
    const cssClasses = useStyles();
    const [userModal, setUserModal] = useState(false);
    const [filteredUser, setFilteredUser] = useState([]);

    useEffect(() => {
        setFilteredUser(activeUser || []);
    }, [activeUser]);

    const closeUserModal = () => {
        setUserModal(false);
        setFilteredUser(activeUser || []);
    };

    const getUserIcon = (usr) => {
        const avatar = getAvatar(get(usr, "config.avatar"));
        return (avatar ? <Avatar size={23} src={avatar} /> : <AccountCircle className={cssClasses.usericonPopUp} />);
    };

    const onSearchUser = (e) => {
        try {
            let txt = e.target.value ? e.target.value : '';
            if (txt && txt.length > 0) {
                txt = txt.toLowerCase().replace(/\s/g, '');
                const val = filter(activeUser || [], act => {
                    const lastName = get(act, "lastName", "").toLowerCase().replace(/\s/g, '');
                    const firstName = get(act, "firstName", "").toLowerCase().replace(/\s/g, '');
                    const username = get(act, "username", "").toLowerCase().replace(/\s/g, '');
                    return lastName.includes(txt) || firstName.includes(txt) || username.includes(txt);
                });
                setFilteredUser(val);
            } else {
                setFilteredUser(activeUser || []);
            }
        } catch (error) {
            setFilteredUser(activeUser || []);
        }
    };

    const handleUserSelect = (usr) => {
        setSelectedUser(usr);
        setUserModal(false);
    };

    return (
        <>
            <ClassAnnouncment onClick={() => setUserModal(true)}>
                <div className='msgSystemNewIcons'><Add className={cssClasses.addIcon} /></div>New message
            </ClassAnnouncment>

            <Modal
                open={userModal}
                onClose={closeUserModal}
                style={{ height: '100%', width: '100%', backgroundColor: "transparent" }}
                className="msgSystemMdlMn"
            >
                <UserModal className="msgSystemMdlUsr">
                    <UserPopUpArea className="msgSystemMdlAra">
                        <Typography variant="h6" className={cssClasses.newMessageTitle}>New Message</Typography>
                        <CloseBtn onClick={closeUserModal}>
                            <Close style={{ color: COLOR.black100, fontWeight: "bolder", fontSize: 25 }} />
                        </CloseBtn>
                        <SearchArea>
                            <SearchIconArea>
                                <SearchOutlined style={{ fontSize: '16px' }} />
                            </SearchIconArea>
                            <SearchInput placeholder='Search students...' onChange={onSearchUser} />
                        </SearchArea>
                        <UserArea style={{ height: '55vh', padding: '8px 0' }}>
                            {filteredUser && filteredUser.length > 0 ? (
                                map(filteredUser, usr => {
                                    return (
                                        <UserList
                                            key={get(usr, "id")}
                                            active={get(selectedUser, "id") === get(usr, "id")}
                                            onClick={() => handleUserSelect(usr)}
                                        >
                                            <UserRow>
                                                <AvtarContainer bgColor={getAvatarColor(get(usr, "id"))}>
                                                    {getUserIcon(usr)}
                                                    {usr.active && <OnlineIndicater />}
                                                </AvtarContainer>
                                                <NameCol>
                                                    <NameInnerRow style={{ color: COLOR.black100, paddingLeft: 12, fontWeight: 600, fontSize: '14px' }}>
                                                        {get(usr, "lastName", "")}, {get(usr, "firstName", "")}
                                                    </NameInnerRow>
                                                </NameCol>
                                            </UserRow>
                                        </UserList>
                                    );
                                })
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    color: COLOR.gray100,
                                    fontSize: '16px',
                                    padding: '40px 20px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        fontSize: '48px',
                                        marginBottom: '16px',
                                        opacity: 0.3
                                    }}>👤</div>
                                    <div style={{ fontWeight: 500, marginBottom: '8px' }}>No students found</div>
                                    <div style={{ fontSize: '14px', opacity: 0.7 }}>Try adjusting your search</div>
                                </div>
                            )}
                        </UserArea>
                    </UserPopUpArea>
                </UserModal>
            </Modal>
        </>
    );
};

export default NewMessageButton;
